import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { X, Mail, Phone, Building2, ExternalLink } from "lucide-react";

const Row = ({ label, value, mono = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(120px, 140px) 1fr",
      gap: 16,
      padding: "12px 0",
      borderBottom: "1px solid rgba(192,192,192,0.1)",
    }}
  >
    <div
      style={{
        color: "#B89B5E",
        fontSize: 10,
        letterSpacing: "0.22em",
        fontWeight: 600,
        textTransform: "uppercase",
        fontFamily: "Montserrat",
      }}
    >
      {label}
    </div>
    <div
      style={{
        color: "#F5F1E8",
        fontSize: 13.5,
        lineHeight: 1.6,
        fontFamily: mono ? "ui-monospace, SF Mono, Menlo, monospace" : "Montserrat",
        wordBreak: "break-word",
      }}
    >
      {value || <span style={{ color: "#666" }}>—</span>}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 28 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <span
        aria-hidden
        style={{ width: 18, height: 1, background: "#B89B5E", opacity: 0.85 }}
      />
      <span
        style={{
          color: "#B89B5E",
          fontFamily: "Montserrat",
          fontSize: 10,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {title}
      </span>
    </div>
    <div>{children}</div>
  </div>
);

const LeadDetailSlideOver = ({ lead, open, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && lead && (
        <>
          <motion.div
            data-testid="lead-detail-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 60,
              backdropFilter: "blur(4px)",
            }}
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={`Lead from ${lead.name}`}
            data-testid="lead-detail-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "min(640px, 100vw)",
              background: "#0F0F0F",
              color: "#F5F1E8",
              borderLeft: "1px solid rgba(192,192,192,0.18)",
              zIndex: 61,
              overflowY: "auto",
              boxShadow: "-24px 0 60px rgba(0,0,0,0.5)",
            }}
          >
            <div
              style={{
                position: "sticky",
                top: 0,
                background: "#0F0F0F",
                borderBottom: "1px solid rgba(192,192,192,0.12)",
                padding: "18px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                zIndex: 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span aria-hidden style={{ width: 6, height: 6, background: "#B89B5E" }} />
                <div
                  style={{
                    fontFamily: "Montserrat",
                    fontSize: 11,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#B89B5E",
                    fontWeight: 600,
                  }}
                >
                  Lead Detail
                </div>
              </div>
              <button
                type="button"
                data-testid="lead-detail-close"
                onClick={onClose}
                aria-label="Close lead detail"
                style={{
                  background: "transparent",
                  border: "1px solid rgba(192,192,192,0.25)",
                  color: "#F5F1E8",
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div style={{ padding: "28px 24px 56px" }}>
              <h2
                style={{
                  margin: 0,
                  fontFamily: "Montserrat",
                  fontSize: 30,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  textTransform: "uppercase",
                }}
              >
                {lead.name}
              </h2>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  marginTop: 14,
                  color: "#C0C0C0",
                  fontSize: 13,
                }}
              >
                <a
                  href={`mailto:${lead.email}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#F5F1E8" }}
                >
                  <Mail size={14} strokeWidth={1.5} /> {lead.email}
                </a>
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#F5F1E8" }}
                  >
                    <Phone size={14} strokeWidth={1.5} /> {lead.phone}
                  </a>
                )}
                {lead.company && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Building2 size={14} strokeWidth={1.5} /> {lead.company}
                  </span>
                )}
              </div>

              <Section title="Project">
                <Row label="Service" value={lead.serviceInterestedIn} />
                <Row label="Requirement" value={lead.requirementType} />
                <Row label="Budget" value={lead.budget} />
                <Row label="Timeline" value={lead.timeline} />
                <Row label="Submitted at" value={lead.submittedAt} />
                <Row label="Submitted page" value={lead.submittedPage} />
              </Section>

              <Section title="Message">
                <div
                  style={{
                    background: "#0A0A0A",
                    border: "1px solid rgba(192,192,192,0.12)",
                    padding: "14px 16px",
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "#F5F1E8",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {lead.message}
                </div>
              </Section>

              <Section title="First-touch attribution">
                <Row label="utm_source" value={lead.first_utm_source} mono />
                <Row label="utm_medium" value={lead.first_utm_medium} mono />
                <Row label="utm_campaign" value={lead.first_utm_campaign} mono />
                <Row label="utm_term" value={lead.first_utm_term} mono />
                <Row label="utm_content" value={lead.first_utm_content} mono />
                <Row
                  label="Referrer"
                  value={
                    lead.first_referrer ? (
                      <a
                        href={lead.first_referrer}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#F5F1E8", display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        {lead.first_referrer}
                        <ExternalLink size={12} strokeWidth={1.5} />
                      </a>
                    ) : null
                  }
                  mono
                />
                <Row label="Landing page" value={lead.first_landing_page} mono />
                <Row label="First seen" value={lead.first_seen_at} mono />
              </Section>

              <Section title="Last-touch attribution">
                <Row label="utm_source" value={lead.last_utm_source} mono />
                <Row label="utm_medium" value={lead.last_utm_medium} mono />
                <Row label="utm_campaign" value={lead.last_utm_campaign} mono />
                <Row label="utm_term" value={lead.last_utm_term} mono />
                <Row label="utm_content" value={lead.last_utm_content} mono />
                <Row
                  label="Referrer"
                  value={
                    lead.last_referrer ? (
                      <a
                        href={lead.last_referrer}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#F5F1E8", display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        {lead.last_referrer}
                        <ExternalLink size={12} strokeWidth={1.5} />
                      </a>
                    ) : null
                  }
                  mono
                />
                <Row label="Landing page" value={lead.last_landing_page} mono />
              </Section>

              <Section title="Technical">
                <Row label="IP" value={lead.ip} mono />
                <Row label="User-agent" value={lead.user_agent} mono />
                <Row label="Lead ID" value={lead.id} mono />
              </Section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default LeadDetailSlideOver;
